import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Snackbar,
  Alert,
} from "@mui/material";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  getRoles,
  getModules,
  createRoleModule,
  getRoleModules,
  deleteRoleModule,
} from "redux/actions/roleMaster";
import { AppState } from "redux/store";
import AppsContainer from "@crema/core/AppsContainer";
import AppInfoView from "@crema/core/AppInfoView";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteMessage from "./DeleteMessage";
import TableHeading from "./TableHeading";

const RoleData = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();

  const { role, modules, roleModules } = useSelector((state: AppState) => state.roleMaster);

  const [formData, setFormData] = useState({ role: "", page: "" });
  const [rows, setRows] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("role");
const [errorSnackbar, setErrorSnackbar] = useState(false);
  const rowsPerPage = 10;
  const [page, setPage] = useState(0);

  useEffect(() => {
    dispatch(getRoles());
    dispatch(getModules());
    dispatch(getRoleModules());
  }, [dispatch]);

  useEffect(() => {
    if (roleModules?.data) {
      const formattedRows = roleModules.data.map((item: any) => ({
        id: item.id,
        role: item.role,
        page: item.module_name,
      }));
      setRows(formattedRows);
    }
  }, [roleModules]);

  const roleOptions = role?.map((item: any) => item.role) || [];
  const pageOptions = modules?.map((item: any) => item.module_name) || [];

  const handleAddOrUpdate = async () => {
    if (!formData.role || !formData.page) return;

    const selectedRole = role.find((item: any) => item.role === formData.role);
    const selectedPage = modules.find((item: any) => item.module_name === formData.page);

    const role_id = selectedRole?.id;
    const module_id = selectedPage?.id;

    try {
      await dispatch(createRoleModule({ role_id, module_id, isActive: 1 }));
      await dispatch(getRoleModules());
 setSuccessSnackbar(true);
      setFormData({ role: "", page: "" });
      setShowForm(false);
      setEditIndex(null);
    } catch (error: any) {
      if (error?.response?.data?.message === "This role-module combination already exists.") {
        alert("This role-module combination already exists.");
      } else {
        alert("Something went wrong.");
      }
    }
  };

  const handleEdit = (row: any, index: number) => {
    setFormData({ role: row.role, page: row.page });
    setShowForm(true);
    setEditIndex(index);
  };

  const handleDelete = (row:any,index: number) => {
    const selectedRow = row;
    console.log("Selected Row for Deletion:", selectedRow);
    setDeleteId(selectedRow.id);
    setOpenDeleteDialog(true);
  };

const handleConfirmDelete = async () => {
  if (deleteId == null) return;

  try {
    await dispatch(deleteRoleModule(deleteId));   // force wait + proper error
    await dispatch(getRoleModules());            // force wait list refresh
    setSuccessSnackbar(true);
  } catch (e) {
    setErrorSnackbar(true);
  } finally {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  }
};

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const sortedRows = [...rows].sort((a, b) => {
    const valA = a[orderBy] ?? "";
    const valB = b[orderBy] ?? "";
    if (valA < valB) return order === "asc" ? -1 : 1;
    if (valA > valB) return order === "asc" ? 1 : -1;
    return 0;
  });

  const paginatedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const commonCellStyle = { fontSize: 14 };
  const roleCellStyles = { width: "200px", ...commonCellStyle };
  const pageCellStyles = { width: "250px", ...commonCellStyle };
  const actionCellStyles = { width: "150px", ...commonCellStyle };

  return (
    <>
      <AppsContainer
        title={
          <Typography sx={{ fontSize: 16, fontWeight: "bold", color: "text.primary", whiteSpace: "nowrap" }}>
            {messages["sidebar.role"] || "Manage Role"}
          </Typography>
        }
        fullView
      >
        <Box sx={{ textAlign: "right", mb: 1, mr: 2, mt: 2 }}>
          <Button
            size="small"
            onClick={() => {
              setShowForm(!showForm);
              setFormData({ role: "", page: "" });
              setEditIndex(null);
            }}
          >
            {showForm ? <RemoveIcon /> : <AddIcon />}
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table size="medium">
            <TableHead sx={{ backgroundColor: "#f4f7fe" }}>
              <TableHeading order={order} orderBy={orderBy} onSort={handleRequestSort} />
            </TableHead>

            <TableBody>
              {showForm && (
                <TableRow
                  sx={{
                    borderBottom: "1px solid #e0e0e0",
                    "& td": { borderBottom: "none" },
                  }}
                >
                  <TableCell sx={commonCellStyle}>{editIndex !== null ? editIndex + 1 : "New"}</TableCell>
                  <TableCell sx={roleCellStyles}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Role</InputLabel>
                      <Select
                        label="Role"
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      >
                        {roleOptions.map((role: string) => (
                          <MenuItem key={role} value={role} sx={commonCellStyle}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell sx={pageCellStyles}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Page</InputLabel>
                      <Select
                        label="Page"
                        value={formData.page}
                        onChange={(e) => setFormData({ ...formData, page: e.target.value })}
                        MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                      >
                        {pageOptions.map((page: string) => (
                          <MenuItem key={page} value={page} sx={commonCellStyle}>
                            {page}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell sx={{ ...actionCellStyles, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddOrUpdate}
                      sx={{ textTransform: "none", minWidth: "70px" }}
                    >
                      {editIndex !== null ? "Update" : "Add"}
                    </Button>
                  </TableCell>
                </TableRow>
              )}

              {paginatedRows.map((row, index) => {
                const actualIndex = page * rowsPerPage + index;
                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      borderBottom: "1px solid #e0e0e0",
                      "& td": { borderBottom: "none" },
                    }}
                  >
                    <TableCell sx={commonCellStyle}>{actualIndex + 1}</TableCell>
                    <TableCell sx={roleCellStyles}>{row.role}</TableCell>
                    <TableCell sx={pageCellStyles}>{row.page || "Not Assigned"}</TableCell>
                    <TableCell sx={{ actionCellStyles, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <IconButton size="small" sx={{ color: "#757575" }} onClick={() => handleEdit(row, actualIndex)}>
                        <EditIcon style={{ fontSize: 24 }} />
                      </IconButton>
                      <IconButton size="small" sx={{ color: "#757575" }} onClick={() => handleDelete(row,actualIndex)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
          />
        </TableContainer>
      </AppsContainer>

      <DeleteMessage
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirmDelete={handleConfirmDelete}
      />
      <Snackbar
        open={successSnackbar}
        autoHideDuration={3000}
        onClose={() => setSuccessSnackbar(false)}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Mapping Updated successfully!
        </Alert>
      </Snackbar>
      <Snackbar
              open={errorSnackbar}
              autoHideDuration={6000}
              onClose={() => setErrorSnackbar(false)}
            >
              <Alert severity="error" sx={{ width: "100%" }}>
                Something went wrong. Please try again!
              </Alert>
            </Snackbar>
      <AppInfoView />
    </>
  );
};

export default RoleData;
